/**
 * Reports — local mode. **Z-6**, plus Z-3's deferred "Audit reports" sub-item.
 *
 * ⚠️ **What this replaces.** `/reports` was a `Placeholder` while the nav promised "Report library ·
 * Savings & ROI · Compliance evidence" — and the audit chain sat hash-verified in IndexedDB with
 * nothing rendering it into a handable artifact. "Compliance evidence" named data we held and never
 * showed.
 *
 * The three promises, read honestly for this device:
 *
 *   • **Report library** — the artifacts this device can actually produce, each with a working
 *     download: the duplicate-groups report and the decision log. A library of two real reports,
 *     not a gallery of mock thumbnails.
 *   • **Compliance evidence** — the audit chain, verified ON GENERATION and downloadable as an
 *     evidence pack. The verification runs when the pack is built, because a pack that asserts
 *     "verified" from a screenshot of a UI is an assertion, not evidence.
 *   • **Savings & ROI** — the countable inputs, and the missing factor NAMED. ADR-045's rule: a
 *     monetary figure without its formula and a tenant-set rate is not shown at all. The rate
 *     configuration (M24/F25) is not built — said in those words, never dressed up as a tier
 *     boundary.
 */
import { useCallback, useState } from 'react';
import { Badge, Button, Callout, Card, DefinitionList, EmptyState, HelpText, Skeleton } from '@dedupr/ui';
import { ErrorState, useQuery } from '@dedupr/app-kit';
import { openLocalStore } from '../local/store.js';
import { downloadCsv, downloadText } from '../local/exportCsv.js';
import { buildDecisionLog, buildEvidencePack, buildGroupsReport, type ReportInput } from '../local/reports.js';
import './Reports.css';

const COUNT = new Intl.NumberFormat();

export function Reports({ onNavigate }: { onNavigate: (route: string) => void }) {
  const [busy, setBusy] = useState<string | null>(null);

  const query = useQuery(async () => {
    const store = await openLocalStore();
    try {
      const run = await store.latestRun();
      const [sources, records, decisions, events] = await Promise.all([
        store.listSources(),
        store.allRecords(),
        store.decisions(),
        store.auditEvents(),
      ]);
      /* Golden rows from EVERY run: an applied merge outlives the run that proposed it, and the
         next upload creates a new run — reading per-run made "Merges applied" drop to 0 the moment
         a new file was loaded, beside an audit card saying four events happened. Found on screen. */
      const [pairs, entities, golden] = run
        ? await Promise.all([store.pairsFor(run.id), store.entitiesFor(run.id), store.allGolden()])
        : [[], [], []];
      return { run, input: { sources, records, pairs, entities, decisions, golden } satisfies ReportInput, events };
    } finally {
      store.close();
    }
  }, []);

  const deliver = useCallback(async (name: string, build: () => Promise<void> | void) => {
    setBusy(name);
    try {
      await build();
    } finally {
      setBusy(null);
    }
  }, []);

  if (query.status === 'loading') {
    return (
      <div className="web-reports" aria-busy="true">
        <Skeleton variant="rect" height="8rem" />
        <Skeleton variant="rect" height="8rem" />
      </div>
    );
  }
  if (query.status === 'error') {
    return <ErrorState code={query.errorCode} message={query.errorMessage} onRetry={query.reload} />;
  }
  const data = query.data;
  if (!data || !data.run) {
    return (
      <EmptyState
        title="Nothing to report on yet"
        description="Load a file and run it, and this screen can produce the duplicate-groups report, the decision log, and the evidence pack."
        action={<Button onClick={() => onNavigate('/local')}>Choose a file</Button>}
      />
    );
  }
  const { input, events } = data;
  const decidedCount = input.decisions.length;
  const liveMerges = input.golden.filter((g) => !g.rolled_back_at);
  const collapsed = liveMerges.reduce((n, g) => n + g.members.length - 1, 0);

  return (
    <div className="web-reports">
      <Card id="reports-library" title="Report library" headingLevel={2}>
        <HelpText>
          Every report is built from what is saved on this device, at the moment you download it —
          nothing is generated in advance, so a report is never staler than the data behind it.
        </HelpText>
        <ul className="web-reports__list">
          <li>
            <div className="web-reports__what">
              <span className="web-reports__name">Duplicate groups</span>
              <span className="web-reports__meta">
                {COUNT.format(input.entities.length)}{' '}
                {input.entities.length === 1 ? 'group' : 'groups'} · which files, which rows, the
                weakest link, what is decided and what is merged
              </span>
            </div>
            <Button
              variant="secondary"
              disabled={busy !== null || input.entities.length === 0}
              onClick={() =>
                void deliver('groups', () =>
                  downloadCsv('dedupr-duplicate-groups.csv', buildGroupsReport(input))
                )
              }
            >
              {busy === 'groups' ? 'Building…' : 'Download CSV'}
            </Button>
          </li>
          <li>
            <div className="web-reports__what">
              <span className="web-reports__name">Decision log</span>
              <span className="web-reports__meta">
                {COUNT.format(decidedCount)} {decidedCount === 1 ? 'verdict' : 'verdicts'} · every
                same/different call a person recorded, with both rows and when
              </span>
            </div>
            <Button
              variant="secondary"
              disabled={busy !== null || decidedCount === 0}
              onClick={() =>
                void deliver('decisions', () =>
                  downloadCsv('dedupr-decision-log.csv', buildDecisionLog(input))
                )
              }
            >
              {busy === 'decisions' ? 'Building…' : 'Download CSV'}
            </Button>
          </li>
        </ul>
        <HelpText>
          A report with nothing in it cannot be downloaded — an empty file that looks like a
          finished report is worse than a disabled button.
        </HelpText>
      </Card>

      <Card id="reports-evidence" title="Compliance evidence" headingLevel={2}>
        {events.length === 0 ? (
          <HelpText>
            No audited change has happened yet. The first applied merge starts the chain — every
            apply and undo is recorded as a hash-linked event, and the pack becomes downloadable
            here the moment one exists.
          </HelpText>
        ) : (
          <>
            <DefinitionList
              items={[
                { term: 'Events recorded', value: COUNT.format(events.length) },
                {
                  term: 'What each carries',
                  value: 'The action, the record ids, the actor, and a link to the event before it',
                  hint: 'Never field values — the log must survive erasure with the evidence intact.',
                },
              ]}
            />
            <Button
              variant="secondary"
              disabled={busy !== null}
              onClick={() =>
                void deliver('evidence', async () =>
                  downloadText(
                    'dedupr-evidence-pack.json',
                    await buildEvidencePack(events),
                    'application/json;charset=utf-8'
                  )
                )
              }
            >
              {busy === 'evidence' ? 'Verifying…' : 'Download the evidence pack'}
            </Button>
            <HelpText>
              The chain is re-verified while the pack is built — the file carries the verdict of
              that check, not an assertion that one once passed.
            </HelpText>
          </>
        )}
      </Card>

      <Card id="reports-savings" title="Savings &amp; ROI" headingLevel={2}>
        <DefinitionList
          items={[
            {
              term: 'Merges applied',
              value: COUNT.format(liveMerges.length),
            },
            {
              term: 'Records collapsed',
              value: COUNT.format(collapsed),
              hint: 'Duplicate records replaced by their surviving record — the quantity a rate would multiply.',
            },
            {
              term: 'Savings figure',
              value: 'Not shown',
              hint: 'Needs your own rate — what a duplicate record costs you per year. A number without that input would be invented.',
            },
          ]}
        />
        {/* ⚠️ Honest, per the queue's rule: the rate configuration is UNBUILT (M24/F25), which is a
            different statement from "not available in local mode". */}
        <Callout tone="info" title="The rate configuration is not built yet">
          Dedupr never shows a money figure without publishing the formula and letting you set the
          rate in it. The screen where a rate is configured (F25&rsquo;s value estimation, M24) is
          not built yet — in any mode. The counts above are the real inputs it will multiply.
        </Callout>
      </Card>
    </div>
  );
}
